/**
 * Early load (contract Amendment C.3, Guillaume 2026-10-11): after the page has loaded and gone
 * idle, read ahead the PUBLIC member-card payloads of the authors in or near the viewport, so a
 * hover card opens with its figures instead of a skeleton.
 *
 * Bounded by construction — ≤ `PREFETCH_LIMIT` reads per page view, `PREFETCH_CONCURRENCY` in
 * flight, distinct handles only, handles already cached skipped, nothing while the tab is hidden —
 * and never on a pointer event: the on-rest warm (`MEMBER_CARD_WARM_DELAY_MS`, Directive 65d57438)
 * stays as the fallback for whatever this did not reach, and is a no-op on a cached handle. Hover
 * devices only: on touch a card never opens, so nothing is read there.
 *
 * One prefetcher per page (a module singleton, `memberCardPrefetcher` in `useMemberCard`): every
 * author trigger registers its element, so lists that mount late (load more, replies) join the
 * same budget, and no page shell has to mount anything. The browser plumbing (`browserPrefetchEnv`)
 * is injected so the scheduling rules are tested without a DOM.
 */

/** Reads per page view, at most (a feed page shows ~20 rows; the first screen is what gets hovered). */
export const PREFETCH_LIMIT = 12
/** Reads in flight at once — leaves the browser's connection pool to the page. */
export const PREFETCH_CONCURRENCY = 2
/** "Near the viewport": about one screen below (and above) the visible area. */
export const PREFETCH_ROOT_MARGIN = '600px'
/** Without `requestIdleCallback` (Safari): this long after `load`. */
export const PREFETCH_IDLE_FALLBACK_MS = 1500

/** The slice of an IntersectionObserver entry the prefetcher reads. */
export interface PrefetchEntry {
  target: Element
  isIntersecting: boolean
  boundingClientRect: { top: number; left: number }
}

export interface PrefetchObserver {
  observe(el: Element): void
  unobserve(el: Element): void
  disconnect(): void
}

/** What the prefetcher reads from the browser — `browserPrefetchEnv()` in the page, fakes in tests. */
export interface PrefetchEnv {
  /** Run `fn` once the page has loaded and gone idle; returns a cancel. */
  afterLoadIdle(fn: () => void): () => void
  /** An observer over `rootMargin`, or null when the browser has none (then nothing is prefetched). */
  observer(onEntries: (entries: PrefetchEntry[]) => void): PrefetchObserver | null
  hidden(): boolean
  /** A hover device: only there can a member card open (touch taps go to the profile). */
  canHover(): boolean
  /** Subscribe to visibility changes; returns an unsubscribe. */
  onVisibility(fn: () => void): () => void
  /** Subscribe to client-side page views (a new page = a new budget); returns an unsubscribe. */
  onPageView(fn: () => void): () => void
}

/** The cache the prefetcher fills — the same one the card reads. */
export interface PrefetchTarget {
  /** Start (or join) the read for `key`; never rejects. */
  load(key: string): Promise<unknown>
  /** True when `key` is answered or in flight (a hover got there first). */
  has(key: string): boolean
}

export interface PrefetchOptions {
  limit?: number
  concurrency?: number
}

export class HandlePrefetcher {
  private readonly limit: number
  private readonly concurrency: number
  private env: PrefetchEnv | null = null
  private readonly registered = new Map<Element, string>()
  /** Handles this page view has queued or read — a failed read is not retried by the prefetcher. */
  private readonly attempted = new Set<string>()
  private readonly queue: string[] = []
  private started = 0
  private inFlight = 0
  private observer: PrefetchObserver | null = null
  private cancelIdle: (() => void) | null = null
  private idleDone = false
  private unsubscribe: Array<() => void> = []
  private readonly target: PrefetchTarget
  private readonly makeEnv: () => PrefetchEnv

  constructor(target: PrefetchTarget, makeEnv: () => PrefetchEnv, options: PrefetchOptions = {}) {
    this.target = target
    this.makeEnv = makeEnv
    this.limit = options.limit ?? PREFETCH_LIMIT
    this.concurrency = options.concurrency ?? PREFETCH_CONCURRENCY
  }

  /** Reads started this page view (test and debugging seam). */
  get reads(): number {
    return this.started
  }

  /** Test seam: forget everything, including the environment. */
  reset(): void {
    this.cancelIdle?.()
    this.cancelIdle = null
    this.stopWatching()
    this.unsubscribe.forEach(u => u())
    this.unsubscribe = []
    this.env = null
    this.registered.clear()
    this.attempted.clear()
    this.queue.length = 0
    this.started = 0
    this.inFlight = 0
    this.idleDone = false
  }

  /** An author trigger's element and handle; returns its unregister (the effect's cleanup). */
  register(el: Element, handle: string): () => void {
    this.registered.set(el, handle.toLowerCase())
    this.boot()
    if (this.observer) {
      this.observer.observe(el)
    }
    return () => {
      this.registered.delete(el)
      this.observer?.unobserve(el)
    }
  }

  private boot(): void {
    if (this.env) {
      return
    }
    const env = this.makeEnv()
    this.env = env
    this.unsubscribe = [
      env.onVisibility(() => this.onVisibility()),
      env.onPageView(() => this.newPage()),
    ]
    this.scheduleIdle()
  }

  private scheduleIdle(): void {
    this.idleDone = false
    this.cancelIdle =
      this.env?.afterLoadIdle(() => {
        this.cancelIdle = null
        this.idleDone = true
        this.watch()
      }) ?? null
  }

  /** Observe every registered trigger — after idle, and again when a hidden tab comes back. */
  private watch(): void {
    const { env } = this
    if (
      !env ||
      !this.idleDone ||
      this.observer ||
      env.hidden() ||
      !env.canHover() ||
      this.exhausted()
    ) {
      return
    }
    this.observer = env.observer(entries => this.onEntries(entries))
    this.registered.forEach((handle, el) => {
      if (!this.attempted.has(handle)) {
        this.observer?.observe(el)
      }
    })
  }

  private onEntries(entries: PrefetchEntry[]): void {
    entries
      .filter(e => e.isIntersecting)
      .sort(
        (a, b) =>
          a.boundingClientRect.top - b.boundingClientRect.top ||
          a.boundingClientRect.left - b.boundingClientRect.left
      )
      .forEach(e => {
        this.observer?.unobserve(e.target)
        const handle = this.registered.get(e.target)
        if (handle) {
          this.enqueue(handle)
        }
      })
    this.pump()
  }

  private enqueue(handle: string): void {
    if (this.attempted.has(handle) || this.exhausted()) {
      return
    }
    if (this.target.has(handle)) {
      // A hover (or an earlier page) already read it: costs no budget.
      return
    }
    this.attempted.add(handle)
    this.queue.push(handle)
  }

  private exhausted(): boolean {
    return this.started + this.queue.length >= this.limit
  }

  private pump(): void {
    while (this.inFlight < this.concurrency && this.queue.length > 0 && !this.env?.hidden()) {
      const handle = this.queue.shift() as string
      if (this.target.has(handle)) {
        continue
      }
      this.started += 1
      this.inFlight += 1
      void this.target.load(handle).finally(() => {
        this.inFlight -= 1
        this.pump()
      })
    }
    if (this.exhausted() && this.queue.length === 0) {
      this.stopWatching()
    }
  }

  private stopWatching(): void {
    this.observer?.disconnect()
    this.observer = null
  }

  /** Hidden: stop — drop the queue, stop observing (reads in flight finish). Visible again:
   *  observe what is left of the budget from where the reader is. */
  private onVisibility(): void {
    if (this.env?.hidden()) {
      this.queue.forEach(h => this.attempted.delete(h))
      this.queue.length = 0
      this.stopWatching()
      return
    }
    this.watch()
  }

  /** A client-side navigation is a new page view: a fresh budget, after idle again. Triggers
   *  stay registered (the new page's mount before or after this event). */
  private newPage(): void {
    this.cancelIdle?.()
    this.stopWatching()
    this.queue.length = 0
    this.attempted.clear()
    this.started = 0
    this.scheduleIdle()
  }
}

/** Run `fn` once the document has loaded and the main thread is idle (fallback: a fixed delay). */
export function afterLoadIdle(fn: () => void): () => void {
  let cancel = (): void => undefined
  const idle = (): void => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(fn, { timeout: 5000 })
      cancel = () => window.cancelIdleCallback(id)
    } else {
      const id = window.setTimeout(fn, PREFETCH_IDLE_FALLBACK_MS)
      cancel = () => window.clearTimeout(id)
    }
  }
  if (document.readyState === 'complete') {
    idle()
  } else {
    const onLoad = (): void => idle()
    window.addEventListener('load', onLoad, { once: true })
    cancel = () => window.removeEventListener('load', onLoad)
  }
  return () => cancel()
}

/** The browser's plumbing; `onPageView` is injected (Next's router events) by the caller. */
export function browserPrefetchEnv(onPageView: PrefetchEnv['onPageView']): PrefetchEnv {
  return {
    afterLoadIdle,
    observer: onEntries => {
      if (typeof IntersectionObserver === 'undefined') {
        return null
      }
      return new IntersectionObserver(entries => onEntries(entries), {
        rootMargin: PREFETCH_ROOT_MARGIN,
      })
    },
    hidden: () => document.visibilityState === 'hidden',
    // The same test as the disclosure group's (`Popover.tsx` canHoverMedia): no hover, no card.
    canHover: () =>
      typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches,
    onVisibility: fn => {
      document.addEventListener('visibilitychange', fn)
      return () => document.removeEventListener('visibilitychange', fn)
    },
    onPageView,
  }
}
